import React from "react";

export default function VpnPolicy() {
  return (
    <div className="min-h-screen bg-gray-100 pb-[160px] font-manrope md:pt-4">
      <div className="container mx-auto bg-white px-6 py-10 shadow-lg md:rounded-lg">
        <h1 className="mb-6 text-center text-4xl font-bold text-blue-600">
          Privacy Policy
        </h1>

        <p className="mb-6 leading-relaxed text-gray-700">
          Security and privacy are at the heart of what we do at GeoVPN.
          Accordingly, we think it is important to be transparent about what
          personal data we collect, how we use it, with whom we share it, and
          how we store it. This Privacy Notice describes how we process and
          handle data provided to us in connection with your use of certain of
          our products, services, apps, and websites that link to this notice
          (we refer to these collectively as our “services”).
        </p>
        <p className="mb-6 leading-relaxed text-gray-700">
          This notice uses the term “personal data” to refer to information that
          is related to an identified or identifiable natural person and is
          protected as personal data under applicable data protection laws.
        </p>
        <p className="mb-6 leading-relaxed text-gray-700">
          This notice covers GeoVPN's data collection and processing practices.
          In this notice, “we,” “us,” and “our” refer to the services provided
          by Global Private Networks, the developer of GeoVPN: Super VPN Proxy.
        </p>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            Age Restrictions
          </h2>
          <p className="leading-relaxed text-gray-700">
            Our services are not intended for and may not be used by minors. In
            this context, minors are individuals under the age of 18. We do not
            knowingly collect personal data from minors. If we discover that we
            have collected personal data from a minor, we may close their
            account and delete associated data without notice.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            Key Assurances about GeoVPN: Super VPN Proxy
          </h2>
          <p className="mb-4 text-gray-700">
            GeoVPN: Super VPN Proxy does not record your VPN browsing activities
            in any way that can be associated with you. When you use a VPN
            connection, we do not store any information that identifies what you
            browse, view, or do online via that VPN connection. The exception is
            when you choose to communicate with us (such as via chat or email)
            over a VPN connection and choose to identify yourself to us.
          </p>
          <p className="mb-4 text-gray-700">
            Data over our VPN connections is encrypted, and we do not inspect or
            record the contents of what you are browsing, viewing, or doing
            through them (unless you are using the VPN connection to communicate
            with us).
          </p>
          <p className="mb-4 text-gray-700">
            Since we do not collect your VPN browsing activities, even if we
            were compelled to share user activity data, there would be nothing
            to give.
          </p>
          <p className="mb-4 text-gray-700">
            Free versions of GeoVPN: Super VPN Proxy may be supported by
            personalized ads, but these ads are not personalized based on any of
            your VPN browsing activity, nor do we share any such activity with
            our advertising partners. Personalization is conducted by our
            advertising partners, who collect personal data such as your
            user-resettable advertising identifier (e.g. AAID or IDFA) and
            combine it with the information they have to show you ads that they
            believe are more relevant to you. Google allows you to disable your
            advertising identifier via Google account settings.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            1. What information do we collect about you?
          </h2>
          <p className="mb-4 text-gray-700">
            This section describes the various types of information we collect
            from and about you. We may collect the types of information
            described below.
          </p>
          <ul className="mb-6 list-inside list-decimal space-y-4 text-gray-700">
            <li>
              <span className="font-semibold">
                Information you provide to us:
              </span>
              <ul className="ml-6 list-inside list-disc space-y-2 text-gray-600">
                <li>
                  <span className="font-semibold">
                    Customer support information:
                  </span>{" "}
                  If you contact us for support, we collect information from you
                  to help diagnose and resolve the issue. This information may
                  include your contact information (e.g., e-mail address),
                  payment information, and information about your use of the
                  services.
                </li>
              </ul>
            </li>
            <li>
              <span className="font-semibold">
                Information collected automatically when using our services
              </span>
              <ul className="mb-[20px] ml-[20px] list-inside list-disc space-y-2 text-gray-700">
                <li>
                  <span className="font-semibold">IP Address:</span> Your IP
                  address is temporarily used to connect you to our VPN servers.
                  We do not log, store, or retain your IP address after the
                  connection is established. We do not track your online
                  activity.
                </li>
                <li>
                  <span className="font-semibold">User interaction data:</span>{" "}
                  We collect information about how you interact with our
                  websites, applications, and advertisements that we may
                  publish. For example, we may track what user interface
                  elements are clicked on.
                </li>
                <li>
                  <span className="font-semibold">Device information:</span> We
                  and our ad networks collect information from and about the
                  device you use to access our services. We may collect device
                  model, device settings, operating system, network information,
                  and application version numbers. Our ad networks only collect
                  this information for free users.
                </li>
                <li>
                  <span className="font-semibold">
                    Advertising identifiers:
                  </span>{" "}
                  Our ad networks may collect your device identifier for
                  marketing and analytics purposes. Google allows you to disable
                  your advertising identifier via Google account settings.
                </li>
              </ul>
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            2. What do we use the information for?
          </h2>
          <ul className="mb-[20px] ml-[20px] list-inside list-disc space-y-4 text-gray-700">
            <li>
              <span className="font-semibold">
                To provide, maintain, troubleshoot, and support our services.
              </span>{" "}
              We use your information since it is required to fulfill our
              contractual obligations to you, such as providing you access when
              you have an active subscription, using IP address information to
              connect you to the best VPN server, using usage information to
              troubleshoot a problem you report with our services, and ensuring
              the proper functioning of our services.
            </li>
            <li>
              <span className="font-semibold">
                To communicate with users and prospective users.
              </span>{" "}
              We use your information to communicate with you, which includes
              responding to your support and other requests, and sending you
              information and updates about our services. We may do this to
              fulfill our contract with you, because you consented to the
              communication, or because we have a legitimate interest in
              providing you with information about our services. You can opt out
              of marketing communications at any time.
            </li>
            <li>
              <span className="font-semibold">To improve our services.</span> To
              continue to improve our services, we use your information to
              understand how users interact with our services. For example, we
              analyze usage, device, and diagnostic information to understand
              usage trends and user engagement with our services. This allows us
              to increase servers where demand has increased. We may use device
              and threat information to conduct spam, threat, and other research
              to improve our threat detection capabilities. We review customer
              feedback to understand what we could be doing better.
            </li>
            <li>
              <span className="font-semibold">IP addresses:</span> We know that
              IP addresses can be regarded by users as sensitive information.
              While collection of your IP address is required from a technical
              perspective to enable the use of our services, IP addresses are
              neither stored nor used beyond the period that they are required
              for such use (i.e. the duration of a VPN session). We do not
              associate IP addresses with any VPN browsing activity.
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            3. Use of Advertising Services With Free VPN Products
          </h2>
          <p className="mb-4 text-gray-700">
            For users of our free VPN products, we may serve ads through the use
            of third-party advertising services. As previously noted, we do not
            collect VPN browsing information or share VPN browsing information
            with advertising service providers. However, as with any online
            advertising service, the entities will have access to the
            information associated with ad delivery such as which website or app
            the ad was delivered to, and certain device-specific information.
          </p>
          <p className="mb-4 text-gray-700">
            To display ads in our apps, we may use third-party software
            development kits (SDKs), which consist of software code provided by
            a third party that is embedded in our apps. These SDKs may collect
            the following information from your device or use:
          </p>
          <ul className="mb-[20px] ml-[20px] list-inside list-disc space-y-2 text-gray-700">
            <li>
              Device or mobile advertising identifiers (controlled by your
              mobile operating system)
            </li>
            <li>
              IP address, which may reveal your approximate geographic location
              as determined by your carrier or ISP. However, when using the VPN
              service, this may replace your actual location with an alternative
              geographic location.
            </li>
            <li>
              Device-specific information such as make, model, OS version,
              language, time zone, and other information.
            </li>
            <li>The name of the app or website serving the ad.</li>
          </ul>
          <p className="mb-4 text-gray-700">
            As some ads may be personalized based on your mobile advertising
            identifier or IP address, you may see an AdChoices icon on an ad.
            You can click this icon to learn more about the ad network that
            provided the ad, its privacy policy, and your choices regarding
            opting out from any personalized advertising. If you opt out from
            personalized advertising, you may still see non-personalized ads.
            While we request you not to use ad blockers to prevent the display
            of ads because that is how we support our free services, our
            services can continue functioning if you do use ad blockers.
          </p>
          <p className="mb-4 text-gray-700">
            Advertising services we use to provide ads with our free VPN
            products may include:
          </p>
          <table className="mb-6 w-full text-gray-600">
            <thead>
              <tr>
                <th className="mb-6 text-left text-[22px] font-semibold">
                  Vendor
                </th>
                <th className="mb-6 text-left text-[22px] font-semibold">
                  Privacy Policy
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Google AdMob / Google Analytics</td>
                <td>
                  <a
                    href="https://www.google.com/url?sa=E&q=https%3A%2F%2Fpolicies.google.com%2Ftechnologies%2Fpartner-sites"
                    className="text-blue-700 underline decoration-blue-700 underline-offset-2"
                  >
                    https://policies.google.com/technologies/partner-sites
                  </a>
                </td>
              </tr>
              <tr>
                <td>IronSource</td>
                <td>
                  <a
                    href="https://www.google.com/url?sa=E&q=https%3A%2F%2Fdevelopers.is.com%2Fironsource-mobile%2Fair%2Fironsource-mobile-privacy-policy%2F"
                    className="text-blue-700 underline decoration-blue-700 underline-offset-2"
                  >
                    https://developers.is.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/
                  </a>
                </td>
              </tr>
              <tr>
                <td>DT Exchange/Fyber</td>
                <td>
                  <a
                    href="https://www.google.com/url?sa=E&q=https%3A%2F%2Fwww.digitalturbine.com%2Fdt-clients-privacy-policy%2F"
                    className="text-blue-700 underline decoration-blue-700 underline-offset-2"
                  >
                    https://www.digitalturbine.com/dt-clients-privacy-policy/
                  </a>
                </td>
              </tr>
              <tr>
                <td>Unity Ads</td>
                <td>
                  <a
                    href="https://www.google.com/url?sa=E&q=https%3A%2F%2Funity.com%2Flegal%2Fprivacy-policy"
                    className="text-blue-700 underline decoration-blue-700 underline-offset-2"
                  >
                    https://unity.com/legal/privacy-policy
                  </a>
                </td>
              </tr>
              <tr>
                <td>LiftOff/Vungle</td>
                <td>
                  <a
                    href="https://www.google.com/url?sa=E&q=https%3A%2F%2Fliftoff.io%2Fprivacy-policy%2F"
                    className="text-blue-700 underline decoration-blue-700 underline-offset-2"
                  >
                    https://liftoff.io/privacy-policy/
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            Third Parties
          </h2>
          <p className="mb-4 text-gray-700">
            We may allow our business partners to place certain tracking
            technologies in our services. These partners use these technologies
            for the following purposes:
          </p>
          <ul className="mb-[20px] ml-[20px] list-inside list-disc space-y-4 text-gray-700">
            <li>
              <span className="font-semibold">To provide our services.</span>{" "}
              Some business partners who help us to provide our services may use
              these technologies to support those efforts.
            </li>
            <li>
              <span className="font-semibold">For Analytics:</span> To help us
              understand how you use our services.
            </li>
            <li>
              <span className="font-semibold">For Marketing:</span> To help us
              market and advertise our services to you, including on third-party
              websites. Cookies are used in connection with this to measure the
              performance of our advertising, attribute actions you take with
              our ads with actions you take on our services, deliver ad
              retargeting (serving ads based on your past interactions with our
              services), and target ads at similar audiences.
            </li>
            <li>
              <span className="font-semibold">To Serve Ads:</span> Ad networks
              may use these technologies to display ads which they think will be
              more relevant to you. For more information, please see the
              “Displaying Advertisements” section above.
            </li>
          </ul>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            4. Your Rights
          </h2>
          <p className="mb-4 text-gray-700">
            Depending on your location of residence, you may have certain legal
            rights in relation to your personal data that we maintain. Subject
            to exceptions and limitations provided by applicable law, these may
            include the right to:
          </p>
          <ul className="mb-[20px] ml-[20px] list-inside list-disc space-y-4 text-gray-700">
            <li>Access and receive a copy of your personal data.</li>
            <li>Correct your personal data.</li>
            <li>Restrict the processing of your personal data.</li>
            <li>Object at any time to the processing of your personal data.</li>
            <li>Have your personal data erased.</li>
            <li>Data portability</li>
            <li>
              Withdraw any consent you previously gave to the processing of your
              data (such as opting out to marketing communications)
            </li>
            <li>lodge a complaint with a data protection authority</li>
            <li>
              Request that we provide you with the categories of personal data
              we collect, disclose or sell about you; the categories of sources
              of such information; the business or commercial purpose for
              collecting or selling your personal data; and the categories of
              third parties with whom we disclose personal data. This
              information may also be provided in this Privacy Policy.
            </li>
          </ul>
          <p className="mb-4 text-gray-700">
            Please note your rights and choices vary depending upon your
            location, and some information may be exempt from certain requests
            under applicable law.
          </p>
          <p className="mb-4 text-gray-700">
            You may be able to exercise some of these rights by using the
            settings and tools provided in our services. For example, you may be
            able to update your user account details via the relevant account
            settings screen of our apps. You may also be able to opt out from
            receiving marketing communications from us by clicking an “opt out”
            or “unsubscribe” link in such communications.
          </p>
          <p className="mb-4 text-gray-700">
            Otherwise, if you wish to exercise any of these rights, you may
            contact us using the details in the “Contact Us” section. As
            permitted by law, we may ask you to verify your identity before
            taking further action on your request.
          </p>
          <p className="mb-4 text-gray-700">
            If you want to delete your account, you may do so at any time by
            contacting us at the email provided in the “Contact Us” section.
            Please note we may retain some personal information for certain
            legal, security, and business needs. For example, we’ll retain
            information about any purchases you may have made through the
            services, and about when you accepted our terms of service.
          </p>
        </div>

        <div className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            5. Privacy Notice Updates
          </h2>
          <p className="leading-relaxed text-gray-700">
            We may update this Privacy Notice from time to time in accordance
            with this section for reasons such as changes in laws, industry
            standards, and business practices. We will post updates to this
            page. If we make updates that materially alter your privacy rights,
            we will also make reasonable efforts to provide you with advance
            notice, such as via email or through the services. If you disagree
            with such an update to this policy, you may stop using the services
            and cancel your services account. If you do not cancel your account
            before the date the update becomes effective, your continued use of
            our services will be subject to the updated Privacy Notice.
          </p>
        </div>

        <div>
          <h2 className="mb-4 text-2xl font-semibold text-blue-600">
            6. Contact Us
          </h2>
          <p className="leading-relaxed text-gray-700">
            We expect this Privacy Policy to evolve over time and welcome
            feedback from our users about our privacy practices. If you have any
            questions or complaints about our privacy practices, you can contact
            us using the following details:
          </p>
          <br />
          <p>
            Email:{" "}
            <a
              href="mailto:support@geovpn.org"
              className="text-blue-700 underline decoration-blue-700 underline-offset-2"
            >
              support@geovpn.org
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
